import { Box, Flex, InputGroup, Input, Text } from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { finleyAssert } from '@finley-technologies/common';

import { inputWidth } from './consts';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  XIconButton,
} from 'packages/common-frontend/components/IconButton';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const _areBindings = (ipt: any): ipt is string[] => {
  if (!Array.isArray(ipt)) {
    return false;
  }
  return ipt.every((el) => typeof el === 'string');
};

const _sanitizeBinding = (binding: string): string => {
  return binding.trim();
};

const BindingView: React.FC<{
  bindingView: string;
  bindingsPropertyKey: string;
  wipConfig?: WipConfig;
  idx: number;
}> = ({ bindingView, wipConfig, bindingsPropertyKey, idx }) => {
  const bindingIdxPropertyKey = `${bindingsPropertyKey}[${idx}]`;

  const [isBindingEditOpen, setBindingEditOpen] = useState<boolean>(false);
  const [binding, setBinding] = useState<string>(bindingView);

  const changeBinding = (): void => {
    if (!wipConfig?.isMutateAllowed(bindingIdxPropertyKey)) {
      return;
    }

    const sanitizedBinding = _sanitizeBinding(binding);
    if (sanitizedBinding.length === 0) {
      return;
    }

    wipConfig.formProps.setFieldValue(bindingIdxPropertyKey, sanitizedBinding);
  };

  const deleteBinding = (): void => {
    if (!wipConfig?.isMutateAllowed(bindingIdxPropertyKey)) {
      return;
    }

    const bindings = getIn(wipConfig.formProps.values, bindingsPropertyKey);
    finleyAssert(
      _areBindings(bindings),
      `Retrieved non-binding structure when utilizing [key:${bindingsPropertyKey}]`,
    );

    wipConfig.formProps.setFieldValue(
      bindingsPropertyKey,
      bindings.filter((_unused, varIdx) => varIdx !== idx),
    );
  };

  return (
    <Flex alignItems="center">
      <Text w="8" mr="2">
        {idx + 1}.
      </Text>

      {isBindingEditOpen &&
      wipConfig?.isMutateAllowed(bindingIdxPropertyKey) ? (
        <>
          <InputGroup w={inputWidth} mr="2">
            <Input
              type="text"
              value={binding}
              onChange={(e) => setBinding(e.target.value)}
            />
          </InputGroup>
          <SaveIconButton
            ariaLabel="save binding"
            label="Save Binding"
            disabled={_sanitizeBinding(binding).length === 0}
            onClick={() => {
              changeBinding();
              setBindingEditOpen(false);
            }}
          />
          <XIconButton
            ariaLabel="close edit"
            label="Close Edit"
            onClick={() => {
              setBinding(bindingView);
              setBindingEditOpen(false);
            }}
          />
        </>
      ) : (
        <>
          <Text width={inputWidth} px="4" mr="2">
            {bindingView}
          </Text>
          {wipConfig?.isMutateAllowed(bindingIdxPropertyKey) && (
            <>
              <EditIconButton
                ariaLabel="edit binding"
                label="Edit Binding"
                onClick={() => setBindingEditOpen(true)}
              />
              <DeleteIconButton
                ariaLabel="delete binding"
                label="Delete Binding"
                onClick={() => deleteBinding()}
              />
            </>
          )}
        </>
      )}
    </Flex>
  );
};

export const BindingContainer: React.FC<{
  wipConfig?: WipConfig;
  bindings: string[];
  bindingsPropertyKey: string;
}> = ({ wipConfig, bindings, bindingsPropertyKey }) => {
  const addBinding = (): void => {
    if (!wipConfig?.isMutateAllowed(bindingsPropertyKey)) {
      return;
    }

    const wipBindings = getIn(wipConfig.formProps.values, bindingsPropertyKey);
    finleyAssert(
      _areBindings(wipBindings),
      `Retrieved non-Athena Bindings structure for [key:${bindingsPropertyKey}]`,
    );

    const bindingsCopy = _.cloneDeep(wipBindings);
    bindingsCopy.push('=Placeholder');
    wipConfig.formProps.setFieldValue(bindingsPropertyKey, bindingsCopy);
  };

  return (
    <Box id="bindings-full-container" mb="5">
      <Flex alignItems="center">
        <Text fontWeight="bold" mr="2">
          Bindings
        </Text>
        {wipConfig?.isMutateAllowed(bindingsPropertyKey) && (
          <AddIconButton
            ariaLabel="add binding"
            label="Add Binding"
            onClick={() => addBinding()}
          />
        )}
      </Flex>

      <Text fontStyle="italic" fontSize="sm" mb="3">
        Bindings starting with &quot;=&quot; will be evaluated as formula
      </Text>

      <Box border="1px" borderColor="gray.700" p="3">
        {bindings.length > 0 ? (
          bindings.map((binding, index) => (
            <BindingView
              key={`binding-${binding}-${uuidv4()}`}
              bindingView={binding}
              wipConfig={wipConfig}
              bindingsPropertyKey={bindingsPropertyKey}
              idx={index}
            />
          ))
        ) : (
          <Text>No bindings configured</Text>
        )}
      </Box>
    </Box>
  );
};
